import {
  Box,
  SectionLabel,
  SegmentedControl,
  Select,
  Text,
  Toggle,
} from '@figma-importers/ui';
import { useImportStore } from '../store/importStore';
import type { ImportMode } from '../types/messages';

const MODES: { value: ImportMode; seg: string; desc: string }[] = [
  {
    value: 'image',
    seg: 'Image',
    desc: 'Pixel-perfect raster of each artboard — works for any file.',
  },
  {
    value: 'vector',
    seg: 'Vector',
    desc: 'Vector paths you can edit — best for simpler artboards.',
  },
  {
    value: 'text',
    seg: 'Live text',
    desc: 'Vectors plus real, editable Figma text layers (fonts matched, not embedded).',
  },
];

// n× maps directly to DPI for PDF user space (1/72 inch). Engine clamps to 4096 px.
const SCALE_OPTIONS = [
  { value: 1, label: 'Screen — 1× (~72 DPI)' },
  { value: 2, label: 'High — 2× (~144 DPI)' },
  { value: 3, label: 'Retina — 3× (~216 DPI)' },
  { value: 4, label: 'Maximum — 4× (~288 DPI)' },
];

export function ModePicker() {
  const mode = useImportStore((s) => s.mode);
  const scale = useImportStore((s) => s.scale);
  const flatten = useImportStore((s) => s.flatten);
  const setMode = useImportStore((s) => s.setMode);
  const setScale = useImportStore((s) => s.setScale);
  const setFlatten = useImportStore((s) => s.setFlatten);

  const desc = MODES.find((m) => m.value === mode)?.desc;

  return (
    <Box display="flex" flexDirection="column" gap="5">
      <Box>
        <SectionLabel>How should we bring it in?</SectionLabel>
        <SegmentedControl
          value={mode}
          onChange={setMode}
          options={MODES.map((m) => ({ value: m.value, label: m.seg }))}
        />
        {desc && (
          <Text mt="2" fontSize="sm" color="figma.textSecondary" lineHeight="snug">
            {desc}
          </Text>
        )}
      </Box>

      {mode === 'image' && (
        <Box>
          <SectionLabel>Image resolution</SectionLabel>
          <Select value={scale} onChange={setScale} options={SCALE_OPTIONS} numeric />
        </Box>
      )}

      <Toggle
        checked={flatten}
        onChange={setFlatten}
        label="Flatten transparency & effects"
        description="Slower, but more faithful for soft masks, blend modes, overprint"
      />
    </Box>
  );
}
